import { Injectable } from '@angular/core';
import { HttpRequestDecorator } from '../core/triggerbee/http.request.decorator';
import { TriggerbeeApi } from '../core/triggerbee/triggerbee-api.enum';
import { Observable } from 'rxjs';
import {
  Audience,
  AudienceFilter,
  AudienceFilterApp,
  AudienceFilterOperator,
  AudienceFilterType,
  AudienceReachType,
} from './components/audience.model';
import { QueryContext, Segment } from 'app/search/query-context';
import { ProfileCount } from 'app/customer-dashboard/customer-dashboard-data.service';
import { Site } from 'app/core/site-context.service';
import { DateRange } from 'app/shared/services/date-service';
import { CriteriaOperator } from 'app/core/operator';
import { TranslateService } from '@ngx-translate/core';
import AudienceHelpers from './audience-helpers';
import { REACH_SUPPORTED_APPS } from '../data-platform/audiences/audience-guesstimate.utils';

@Injectable({
  providedIn: 'root',
})
export class AudienceReachService {
  constructor(
    private http: HttpRequestDecorator,
    private readonly translateService: TranslateService,
  ) {}

  public populateAudienceReach(site: Site, audiences: Array<Audience>) {
    if (audiences) {
      audiences.forEach((audience: Audience) => {
        this.populateReachOnAudience(audience, audiences);
      });
    }
  }

  public populateReachOnAudience(audience: Audience, audiences: Array<Audience>, onlyNumber = false) {
    const request = new QueryContext();
    request.segment = new Segment();
    request.segment.criterias = {};
    request.dateRange = new DateRange();

    if (audience.isDefault) {
      // TODO: Remove this when default audiences are gone
      if (
        audience.name === 'audience.default.identified' ||
        audience.name === 'Identified' ||
        audience.name === 'Identifierade'
      ) {
        request.segment.criterias['HasVisitor'] = {
          operator: CriteriaOperator.Equals,
          value: true,
        };
      } else if (
        audience.name === 'audience.default.notIdentified' ||
        audience.name === 'Not Identified' ||
        audience.name === 'Icke Identifierad'
      ) {
        request.segment.criterias['HasVisitor'] = {
          operator: CriteriaOperator.Equals,
          value: false,
        };
      } else if (
        audience.name === 'audience.default.consent' ||
        audience.name === 'Has Given Any Consent' ||
        audience.name === 'Har gett något samtycke'
      ) {
        request.segment.criterias['consentVersions'] = {
          operator: CriteriaOperator.Any,
          value: null,
        };
      } else if (
        audience.name === 'audience.default.withoutConsent' ||
        audience.name === 'Has Not Given Any Consent' ||
        audience.name === 'Har inte gett något samtycke'
      ) {
        request.segment.criterias['consentVersions'] = {
          operator: CriteriaOperator.NotAny,
          value: null,
        };
      }
    } else {
      if (audience.filters) {
        audience.filters.forEach((filter: AudienceFilter) => {
          if (filter.type === 'Consent' && filter.operator === 'Exists') {
            if (filter.value === 'true') {
              request.segment.criterias[this.getCriteriaName(filter.type)] = {
                operator: CriteriaOperator.Any,
                value: null,
              };
            } else {
              request.segment.criterias[this.getCriteriaName(filter.type)] = {
                operator: CriteriaOperator.NotAny,
                value: null,
              };
            }
          } else {
            request.segment.criterias[this.getCriteriaName(filter.type!)] = {
              operator: this.getOperator(filter.operator!),
              value: this.getValue(filter),
              dateRange: AudienceHelpers.getDateRangeFromFilter(filter),
              frequencyType: filter.frequencyType,
              frequencyValue: filter.frequencyValue,
            };
          }
        });
      }
    }

    if (
      audience.filters &&
      AudienceHelpers.isAudienceReachSupported(audience.filters, audience.sessionConditions ?? [])
    ) {
      const reachApp = audience.filters[0]?.app;
      if (!!reachApp && REACH_SUPPORTED_APPS.includes(reachApp)) {
        this.getIntegrationsReach(reachApp, audience.filters[0].value).subscribe({
          // Null/error → -1 (N/A); leaving reach undefined strands the card on "calculating".
          next: (result) => {
            audience.reach = result ?? -1;
            audience.reachType = AudienceReachType.Total;
          },
          error: () => {
            audience.reach = -1;
          },
        });
      } else {
        // Add default date filtering so we only get results for last week
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);
        (request.dateRange as DateRange).startDate = startDate.toDateString();

        // Do count query
        this.countProfilesByQueryContext(request).subscribe((result: any) => {
          if (onlyNumber === true) {
            audience.reach = result.data;
            audience.reachType = undefined;
          } else {
            audience.reach = result.data;
            audience.reachType = AudienceHelpers.isInternalAudience(audience.filters)
              ? AudienceReachType.Weekly
              : AudienceReachType.Total;
          }
          if (audiences) {
            const audienceIndex = audiences.findIndex((a) => a.id === audience.id);
            if (audienceIndex !== -1) {
              audiences[audienceIndex].reach = audience.reach;
            }
          }
        });
      }
    } else {
      audience.reach = -1;
      if (audiences) {
        const audienceIndex = audiences.findIndex((a) => a.id === audience.id);
        if (audienceIndex !== -1) {
          audiences[audienceIndex].reach = -1;
        }
      }
    }
  }

  public getTotalAudiencePotentialForLast7Days(): Observable<any> {
    const request = new QueryContext();
    request.segment = new Segment();
    request.segment.criterias = {};
    request.dateRange = new DateRange();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    (request.dateRange as DateRange).startDate = startDate.toDateString();

    return this.countProfilesByQueryContext(request);
  }

  private generateAudienceReachOperator(input: number): string {
    const largeNumber = 1000;
    const tooSmall = 10;
    if (input >= largeNumber) {
      return (Math.round(input / largeNumber) * largeNumber).toLocaleString();
    }
    if (input <= tooSmall) {
      return '< ' + tooSmall;
    }
    return this.translateService.instant('audience.reach.below') + ' ' + largeNumber;
  }

  private generateAudienceReachTypeText(filters: AudienceFilter[]): string {
    if (AudienceHelpers.isInternalAudience(filters)) {
      return this.translateService.instant('audience.reach.weekly');
    }
    return this.translateService.instant('audience.reach.total');
  }

  private getCriteriaName(type: AudienceFilterType): string {
    switch (type) {
      case AudienceFilterType.Tags: {
        return 'Labels';
        break;
      }
      case AudienceFilterType.Identified: {
        return 'HasVisitor';
        break;
      }
      case AudienceFilterType.InterestProfiles: {
        return 'ValidProfilings';
        break;
      }
      case AudienceFilterType.Consent: {
        return 'consentVersions';
        break;
      }
    }
    return type;
  }

  private getOperator(operator: AudienceFilterOperator): CriteriaOperator {
    switch (operator) {
      case AudienceFilterOperator.Equals: {
        return CriteriaOperator.Equals;
        break;
      }
      case AudienceFilterOperator.NotEquals: {
        return CriteriaOperator.NotEquals;
        break;
      }
    }
    return CriteriaOperator.Equals;
  }

  private getValue(filter: AudienceFilter): any {
    if (filter.type === AudienceFilterType.Identified) {
      return filter.value;
    }
    return [filter.value];
  }

  public countProfilesByQueryContext(queryContext: QueryContext): Observable<{ error: any; data: number }> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/profiles/count?useCache=true';
    return this.http.post(url, queryContext);
  }

  public getProfileCountBySiteIds(
    siteIds: number[],
    queryContext: QueryContext,
  ): Observable<{ error: unknown; data: ProfileCount[] }> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/profiles/count/by-sites?useCache=true`;
    return this.http.post(url, { ...queryContext, siteIds });
  }

  public getIntegrationsReach(integration: AudienceFilterApp, listId: string): Observable<number> {
    const url =
      this.http.getBaseUrl(TriggerbeeApi.Gateway) +
      `/integrations/reach/${integration}?listId=${encodeURIComponent(listId)}`;
    return this.http.get(url);
  }
}
